import { useEffect, useState } from "react";
import api from "../lib/api";
import { Newspaper, ArrowSquareOut, ArrowsClockwise, TrendUp, TrendDown, Minus, FunnelSimple } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const SENTIMENT_TONE = {
  positive: { icon: TrendUp, color: "text-[hsl(var(--secondary))]", label: "Positive" },
  negative: { icon: TrendDown, color: "text-destructive", label: "Watch out" },
  neutral: { icon: Minus, color: "text-muted-foreground", label: "Neutral" },
};

/**
 * Property news — curated RSS + Claude one-line summaries.
 * Public read; admin can trigger refresh.
 */
export default function News() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sentiment, setSentiment] = useState("");
  const [topic, setTopic] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "60" });
      if (sentiment) params.set("sentiment", sentiment);
      if (topic) params.set("topic", topic);
      const { data } = await api.get(`/news/feed?${params.toString()}`);
      setItems(data.items || []);
    } catch (err) {
      // Network or API error — surface toast and keep current items
      toast.error("Couldn't load news");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentiment, topic]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/news/refresh");
      toast.success(`+${data.newly_inserted} new headlines`);
      await load();
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-12" data-testid="news-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Newspaper size={14} weight="duotone" /> Property news
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl">What's moving the market.</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Curated headlines from Indian real-estate desks — each line summarised by AI so you skim what matters in 30 seconds.
          </p>
        </div>
        {user?.role === "admin" && (
          <button
            onClick={refresh}
            disabled={refreshing}
            className="btn-ghost px-4 py-2 text-xs inline-flex items-center gap-2"
            data-testid="news-refresh"
          >
            <ArrowsClockwise size={14} weight={refreshing ? "fill" : "duotone"} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing…" : "Refresh feed"}
          </button>
        )}
      </div>

      <div className="card-flat p-4 mb-6 flex gap-3 items-end flex-wrap text-xs">
        <FunnelSimple size={14} weight="duotone" className="self-center text-muted-foreground" />
        <label>
          <span className="eyebrow block mb-1">Tone</span>
          <select
            value={sentiment}
            onChange={(e) => setSentiment(e.target.value)}
            className="input-dark px-3 py-2 text-xs"
            data-testid="news-filter-sentiment"
          >
            <option value="">Any</option>
            <option value="positive">Positive only</option>
            <option value="negative">Watch out</option>
            <option value="neutral">Neutral</option>
          </select>
        </label>
        <label>
          <span className="eyebrow block mb-1">Topic</span>
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="input-dark px-3 py-2 text-xs"
            data-testid="news-filter-topic"
          >
            <option value="">All</option>
            <option value="general">Market</option>
            <option value="policy">Policy / RBI</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="card-flat p-12 text-center text-muted-foreground" data-testid="news-loading">Loading headlines…</div>
      ) : items.length === 0 ? (
        <div className="card-flat p-12 text-center" data-testid="news-empty">
          <Newspaper size={28} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">
            No news yet. {user?.role === "admin" ? "Click 'Refresh feed' to fetch the latest." : "Check back shortly."}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="news-list">
          {items.map((item) => {
            const tone = SENTIMENT_TONE[item.sentiment] || SENTIMENT_TONE.neutral;
            const ToneIcon = tone.icon;
            return (
              <a
                key={item.id}
                href={item.link}
                target="_blank"
                rel="noreferrer"
                className="card-flat p-5 block hover:border-[hsl(var(--secondary))]/40 transition-colors"
                data-testid={`news-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-serif text-lg sm:text-xl mb-1.5 leading-tight">{item.title}</h3>
                    {item.summary_ai && (
                      <p className="text-sm text-muted-foreground mb-2">{item.summary_ai}</p>
                    )}
                    <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <span className={`inline-flex items-center gap-1 ${tone.color}`}>
                        <ToneIcon size={11} weight="bold" /> {tone.label}
                      </span>
                      <span>·</span>
                      <span>{item.source}</span>
                      <span>·</span>
                      <span className="capitalize">{item.topic}</span>
                      {item.published && (
                        <>
                          <span>·</span>
                          <span>{item.published.split(",")[0] || item.published.slice(0, 16)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <ArrowSquareOut size={18} weight="duotone" className="text-muted-foreground shrink-0 mt-1" />
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
